<div class="flex flex-col max-w-2xl w-full">
  <!-- Header -->
  <div class="flex items-start justify-between pb-5 border-b border-gray-4">
    <div class="flex items-center gap-3">
      <div class="w-10 h-10 rounded-lg bg-indigo-a3 text-indigo-11 flex items-center justify-center">
        <mat-icon class="text-xl">group_add</mat-icon>
      </div>
      <div>
        <h2 class="text-lg font-bold text-gray-12 m-0">Co-gestionnaires et invitations</h2>
        <p class="text-xs text-gray-10 m-0 mt-0.5">
          Partagez la gestion du bien avec vos proches, indivisaires ou ex-conjoints pour suivre ensemble les
          candidatures.
        </p>
      </div>
    </div>
    <button
      mat-icon-button
      type="button"
      (click)="close()"
      aria-label="Fermer la boîte de dialogue"
      class="text-gray-10 hover:text-gray-12"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Content -->
  <div class="py-5 flex flex-col gap-6 max-h-[70vh] overflow-y-auto">
    <!-- Section: Liste des membres -->
    <div class="flex flex-col gap-3">
      <h3 class="text-sm font-semibold text-gray-12 m-0 flex items-center gap-2">
        <mat-icon class="text-base text-gray-10">people</mat-icon>
        <span>Membres ayant accès au bien</span>
      </h3>

      <div class="flex flex-col gap-2">
        <!-- Propriétaire référent (Créateur) -->
        @if (owner(); as propOwner) {
          <div class="flex items-center justify-between p-3 rounded-lg bg-gray-2 border border-gray-4">
            <div class="flex items-center gap-3">
              @if (propOwner.identity?.avatar?.url) {
                <img
                  [src]="propOwner.identity?.avatar?.url"
                  [alt]="(propOwner.identity?.firstName || '') + ' ' + (propOwner.identity?.lastName || '')"
                  class="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-4"
                />
              } @else {
                <div
                  class="w-9 h-9 rounded-full bg-indigo-a4 text-indigo-12 font-bold text-xs flex items-center justify-center shrink-0"
                >
                  {{ propOwner.identity?.firstName?.[0] || 'P'
                  }}{{ propOwner.identity?.lastName?.[0] || 'R' }}
                </div>
              }
              <div class="flex flex-col">
                <span class="text-sm font-semibold text-gray-12">
                  {{ propOwner.identity?.firstName }} {{ propOwner.identity?.lastName }}
                </span>
                <span class="text-xs text-gray-10">{{ propOwner.contactInfos?.email }}</span>
              </div>
            </div>
            <span class="px-2.5 py-1 text-xs font-semibold rounded-md bg-indigo-a3 text-indigo-11">
              Propriétaire référent
            </span>
          </div>
        }

        <!-- Co-gestionnaires & Invités -->
        @for (member of members(); track $index) {
          @let user = getMemberUser(member);
          <div class="flex items-center justify-between p-3 rounded-lg bg-gray-2 border border-gray-4">
            <div class="flex items-center gap-3">
              @if (user?.identity?.avatar?.url) {
                <img
                  [src]="user?.identity?.avatar?.url"
                  [alt]="getMemberDisplayName(member)"
                  class="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-4"
                />
              } @else {
                <div
                  class="w-9 h-9 rounded-full bg-gray-4 text-gray-12 font-bold text-xs flex items-center justify-center shrink-0"
                >
                  {{ getMemberInitials(member) }}
                </div>
              }
              <div class="flex flex-col">
                <span class="text-sm font-semibold text-gray-12">
                  {{ getMemberDisplayName(member) }}
                </span>
                <span class="text-xs text-gray-10">
                  {{ user?.contactInfos?.email || 'Invitation par email' }}
                </span>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <!-- Rôle badge -->
              <span
                class="px-2.5 py-0.5 text-xs font-medium rounded-md"
                [class]="
                  member.role === PropertyMemberRole.CO_OWNER
                    ? 'bg-indigo-a2 text-indigo-11 border border-indigo-a4'
                    : 'bg-gray-3 text-gray-11 border border-gray-5'
                "
              >
                {{ member.role === PropertyMemberRole.CO_OWNER ? 'Co-vendeur' : 'Spectateur' }}
              </span>

              <!-- Statut badge -->
              <span
                class="px-2.5 py-0.5 text-xs font-medium rounded-md"
                [class]="
                  member.status === PropertyMemberStatus.ACCEPTED
                    ? 'bg-jade-a3 text-jade-11'
                    : 'bg-amber-a3 text-amber-11'
                "
              >
                {{ member.status === PropertyMemberStatus.ACCEPTED ? 'Actif' : 'En attente' }}
              </span>

              <!-- Action retirer -->
              @if (isOwner()) {
                <button
                  mat-icon-button
                  type="button"
                  (click)="removeMember(member)"
                  [disabled]="removingUserId() !== null"
                  title="Retirer l'accès"
                  aria-label="Retirer l'accès à ce membre"
                  class="text-gray-10 hover:text-red-11"
                >
                  <mat-icon class="text-lg">delete_outline</mat-icon>
                </button>
              }
            </div>
          </div>
        }

        @if (members().length === 0 && !property().owner) {
          <div
            class="p-4 text-center text-xs text-gray-10 bg-gray-2 rounded-lg border border-dashed border-gray-4"
          >
            Aucun co-gestionnaire n'a encore été invité.
          </div>
        }
      </div>
    </div>

    <!-- Section: Inviter un nouveau membre -->
    @if (isOwner()) {
      <div class="flex flex-col gap-4 pt-4 border-t border-gray-4">
        <h3 class="text-sm font-semibold text-gray-12 m-0 flex items-center gap-2">
          <mat-icon class="text-base text-gray-10">mail_outline</mat-icon>
          <span>Inviter un nouveau membre</span>
        </h3>

        <form (ngSubmit)="sendInvitation()" class="flex flex-col gap-3">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Prénom </mat-label>
              <input matInput [formField]="formTree.firstName" placeholder="Ex. Sophie" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Nom </mat-label>
              <input matInput [formField]="formTree.lastName" placeholder="Ex. Martin" />
            </mat-form-field>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <mat-form-field appearance="outline" class="sm:col-span-2 w-full">
              <mat-label>Adresse email du co-vendeur</mat-label>
              <input
                matInput
                type="email"
                [formField]="formTree.email"
                placeholder="Ex. sophie.martin@email.fr"
              />
              <mat-icon matSuffix class="text-gray-10">alternate_email</mat-icon>
              @if (formTree.email().errors().length && formTree.email().touched()) {
                <mat-error>Une adresse email valide est requise</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Rôle attribué</mat-label>
              <mat-select [formField]="formTree.role">
                <mat-option #optCoOwner [value]="PropertyMemberRole.CO_OWNER">
                  <div class="flex flex-col py-1">
                    <span
                      [class]="optCoOwner.selected ? 'text-white' : 'text-neutral-12'"
                      class="font-medium text-sm"
                    >
                      Co-vendeur
                    </span>
                  </div>
                </mat-option>
                <mat-option #optViewer [value]="PropertyMemberRole.VIEWER">
                  <div class="flex flex-col py-1">
                    <span
                      [class]="optViewer.selected ? 'text-white' : 'text-neutral-12'"
                      class="font-medium text-sm"
                    >
                      Spectateur
                    </span>
                  </div>
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <!-- Explication dynamique du rôle sélectionné -->
          <div class="p-3 rounded-lg bg-indigo-a2 border border-indigo-a4 text-xs flex items-start gap-2.5">
            <mat-icon class="text-indigo-9 text-base shrink-0 mt-0.5">info</mat-icon>
            <div class="text-indigo-12 leading-relaxed">
              @if (selectedRole() === PropertyMemberRole.CO_OWNER) {
                <div>
                  <span class="font-semibold">Co-vendeur (accès complet) :</span>
                  Ce rôle permet de modifier toutes les informations du bien (description, photos,
                  caractéristiques), de téléverser des documents et de participer activement au suivi des
                  candidatures.
                </div>
              } @else {
                <div>
                  <span class="font-semibold">Spectateur (lecture seule) :</span>
                  Ce rôle permet de consulter l'ensemble du dossier du bien et de suivre les candidatures,
                  sans pouvoir modifier l'annonce ni supprimer de documents.
                </div>
              }
            </div>
          </div>

          <div class="flex items-center justify-between pt-1">
            <span class="text-xs text-gray-10">
              Un email contenant un lien sécurisé d'activation lui sera expédié.
            </span>
          </div>
        </form>
      </div>
    }
  </div>

  <!-- Footer -->
  <div class="flex items-center justify-end pt-4 border-t border-gray-4 gap-4">
    <button
      mat-flat-button
      type="button"
      [disabled]="!isFormValid() || isInviting()"
      (click)="sendInvitation()"
      class="primary"
    >
      <mat-icon class="mr-1.5 text-base">send</mat-icon>
      <span>{{ isInviting() ? 'Envoi en cours...' : "Envoyer l'invitation" }}</span>
    </button>
    <button mat-button type="button" (click)="close()" class="text-gray-12">Fermer</button>
  </div>
</div>
